import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { UserProfile } from '../types/user';
import { DEFAULT_PRICING } from '../config/constants';
import { defaultTheme } from '../types/theme';

const defaultUserData: UserProfile = {
  username: '',
  email: '',
  createdAt: new Date().toISOString(),
  pricingConfig: DEFAULT_PRICING,
  settings: {
    companyName: '',
    applicationName: 'Print Shop Calculator',
    applicationTagline: 'Get instant pricing for your custom apparel',
    logoLightMode: '',
    logoDarkMode: '',
    copyrightText: `© ${new Date().getFullYear()} · All rights reserved`,
    adminName: '',
    adminEmail: ''
  },
  customization: {
    theme: defaultTheme,
    domain: {
      customDomain: '',
      verified: false,
      lastVerificationAttempt: null
    }
  }
};

export function useUserData(userId: string) {
  const [userData, setUserData] = useState<UserProfile>(defaultUserData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const docRef = doc(db, 'users', userId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data() as UserProfile;
          // Ensure customization object exists with defaults
          setUserData({
            ...data,
            customization: {
              theme: data.customization?.theme || defaultTheme,
              domain: data.customization?.domain || {
                customDomain: '',
                verified: false,
                lastVerificationAttempt: null
              }
            }
          });
        } else {
          // Initialize user data if it doesn't exist
          await setDoc(docRef, defaultUserData);
          setUserData(defaultUserData);
        }
      } catch (err) {
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const updateUserData = async (newData: Partial<UserProfile>) => {
    try {
      const updatedData = { ...userData, ...newData };
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, updatedData);
      setUserData(updatedData);
    } catch (err) {
      throw new Error('Failed to update user data');
    }
  };

  return { userData, setUserData: updateUserData, loading, error };
}